import { cx } from '@twind/core';
import { useAnalytics } from 'app/context/Analytics';
import { PosthogEventName } from 'app/copied/constants/PosthogEventName';
import useIndicationSelector from 'app/hooks/useIndicationSelector';
import useRxConditionId from 'app/hooks/useRxConditionId';
import useShouldAutoOpen from 'app/modules/tailor/hooks/useShouldAutoOpen';
import { stripHTML } from 'app/utils/stripHTML';

import DropdownMenu, { DropdownMenuItemInterface, DropdownMenuItemProps } from './DropdownMenu';
import OstroDynamicContent from './OstroDynamicContent';

const IndicationSelectorMenuItem = ({
  item,
  onItemSelect,
  className,
  underlineColor,
  style,
  isActive,
}: DropdownMenuItemProps) => {
  return (
    <li
      className={cx(
        '~(group flex items-start justify-between px-4 py-3 cursor-pointer text-gray-700)',
        className,
        'ostro-tailor-indication-selector-menu-item'
      )}
      onClick={() => onItemSelect?.(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onItemSelect?.(item);
        }
      }}
      tabIndex={onItemSelect ? 0 : -1}
    >
      <div className="w-full text-left">
        <OstroDynamicContent
          style={{
            textDecorationColor: underlineColor,
            ...style,
          }}
          className={cx(
            '~(text-sm font-semibold group-hover:underline decoration-[1.75px] [&_p]:mb-1)',
            isActive ? 'underline' : ''
          )}
          content={item.name}
        />
        <OstroDynamicContent
          className="text-xs [&_p]:mb-0"
          content={item.description}
        />
      </div>
    </li>
  );
};

interface PrimaryIndicationSelectorProps {
  className?: string;
}

const PrimaryIndicationSelector = ({ className }: PrimaryIndicationSelectorProps) => {
  const [conditionId] = useRxConditionId();
  const { indicationSelectorModule, primaryIndicationSelectorEnabled, getSelectedIndication } = useIndicationSelector();
  const { captureEventWithActiveScreen } = useAnalytics();
  const [, setShouldAutoOpen] = useShouldAutoOpen();

  const handleOpen = () => {
    captureEventWithActiveScreen(PosthogEventName.IndicationMenuOpened);
  };
  const handleItemSelect = (item: DropdownMenuItemInterface) => {
    captureEventWithActiveScreen(PosthogEventName.IndicationChanged, {
      indication_selector_text: stripHTML(item.name),
      selectorLocation: 'Primary',
      currentConditionId: conditionId,
      newConditionId: item.id,
    });
    window?.TAIlor?.reloadWithId(item.id);
    setShouldAutoOpen(true);
  };
  if (!primaryIndicationSelectorEnabled) return null;

  const selectedIndication = getSelectedIndication();

  return (
    <DropdownMenu
      selectedItem={{
        name: selectedIndication?.indication_name ?? '',
        description: selectedIndication?.description ?? '',
        id: selectedIndication?.hub_condition_uuid ?? '',
        displayName: selectedIndication?.display_name ?? '',
      }}
      id="ostro-tailor-primary-indication-selector"
      title={indicationSelectorModule?.header_title ?? ''}
      items={
        indicationSelectorModule?.items?.map((item) => ({
          name: item.indication_name ?? '',
          description: item.description ?? '',
          id: item.hub_condition_uuid ?? '',
          displayName: item.display_name ?? '',
          underlineColor: item.border_color,
          style: {
            ...(conditionId === item.hub_condition_uuid ? { textDecoration: 'underline' } : {}),
          },
        })) ?? []
      }
      onItemSelect={handleItemSelect}
      className={className}
      disclaimer={indicationSelectorModule?.disclaimer_text}
      disclaimerEnabled={!!indicationSelectorModule?.disclaimer_enabled}
      menuItemComponent={IndicationSelectorMenuItem}
      onOpen={handleOpen}
      buttonClassName={cx(
        'px-3 py-2',
        `${
          selectedIndication
            ? `border-[${selectedIndication.border_size}px] border-[${selectedIndication.border_color}] bg-[${selectedIndication.background_color}] rounded-[${selectedIndication.border_radius}px]`
            : ''
        }`
      )}
    />
  );
};

const SecondaryIndicationSelector = ({ className }: { className?: string }) => {
  const [conditionId] = useRxConditionId();
  const { secondaryIndicationSelectorEnabled, indicationSelectorModule } = useIndicationSelector();
  const { captureEventWithActiveScreen } = useAnalytics();
  const [, setShouldAutoOpen] = useShouldAutoOpen();
  const handleItemSelect = (item: DropdownMenuItemInterface) => {
    captureEventWithActiveScreen(PosthogEventName.IndicationChanged, {
      indication_selector_text: stripHTML(item.name),
      selectorLocation: 'Secondary',
    });
    window?.TAIlor?.reloadWithId(item.id);
    setShouldAutoOpen(true);
  };

  if (!secondaryIndicationSelectorEnabled) return null;

  return (
    <div
      id="ostro-tailor-secondary-indication-selector"
      className={className}
    >
      <OstroDynamicContent content={indicationSelectorModule?.header_title} />
      <ul>
        {indicationSelectorModule?.items?.map((item) => (
          <IndicationSelectorMenuItem
            key={item.hub_condition_uuid}
            item={{
              name: item.indication_name ?? '',
              description: item.description ?? '',
              id: item.hub_condition_uuid ?? '',
            }}
            onItemSelect={handleItemSelect}
            className="px-4 pl-0"
            underlineColor={item.border_color}
            isActive={conditionId === item.hub_condition_uuid}
          />
        ))}
      </ul>
      {indicationSelectorModule?.disclaimer_enabled && indicationSelectorModule?.disclaimer_text && (
        <IndicationSelectorMenuItem
          item={{ name: '', description: indicationSelectorModule?.disclaimer_text, id: '' }}
          onItemSelect={() => {}}
          className="px-4 pl-0 cursor-default"
        />
      )}
    </div>
  );
};

export { PrimaryIndicationSelector, SecondaryIndicationSelector };
